import classNames from 'classnames';
import { FC, useMemo } from 'react';

import { BackgroundImage, Box, Button, Center, Flex, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { PlaceMap } from '@/features/PlaceMap';

import useAppStore from '@/shared/store/app.store';
import useBookingStore from '@/shared/store/booking.store.ts';
import { TDateRange } from '@/shared/types/app.types';
import { IBookingObjects } from '@/shared/types/bookingObjects.types';
import IconMap from '@/shared/uikit/assets/icons/IconMap.svg?react';
import IconMapPin from '@/shared/uikit/assets/icons/IconMapPin.svg?react';
import MapTemp from '@/shared/uikit/assets/images/MapTemp.svg';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';

import classes from './MapButton.module.css';

type MapButtonProps = {
	objects?: IBookingObjects;
	text: string;
	date?: TDateRange;
	currentLocation?: string;
	objectType?: string;
	disabled?: boolean;
	showOnMap?: boolean;
	onSave?: (place: IBookingObjects) => void;
};

export const MapButton: FC<MapButtonProps> = ({
	text,
	date,
	objects,
	currentLocation,
	objectType = '',
	disabled = false,
	showOnMap = false,
	onSave,
}) => {
	const isMobile = useAppStore((state) => state.isMobile);
	const bookingPlace = useBookingStore((state) => state.bookingPlace);
	const [isOpened, { open, close }] = useDisclosure(false);

	const handleSave = () => {
		if (bookingPlace) {
			if (onSave) {
				onSave(bookingPlace);
			}
			close();
		}
	};

	const buttons = useMemo(() => {
		if (showOnMap) {
			return [
				{
					children: 'Закрыть',
					onClick: close,
				},
			];
		}

		if (bookingPlace) {
			return [
				{
					children: 'Выбрать место',
					onClick: handleSave,
				},
			];
		}

		return [
			{
				children: 'Место не выбрано',
				variant: 'light',
			},
		];
	}, [showOnMap, bookingPlace]);

	if (isMobile)
		return (
			<>
				<Box>
					<BackgroundImage
						className={classNames({
							[classes.disabled]: disabled,
						})}
						src={MapTemp}
						radius="lg"
						h={100}
					>
						<Center p="md" className={classes.root}>
							<Button c="black" variant="white" size="compact-md" radius="lg">
								<Flex py={4} gap={4} align="center" px={'xs'}>
									<IconMap />
									<Text fz={14} fw={600} lh="18px" onClick={open}>
										{text}
									</Text>
								</Flex>
							</Button>
						</Center>
					</BackgroundImage>
				</Box>

				<BottomSheet
					opened={isOpened}
					onClose={close}
					withCloseButton={false}
					buttonsDirection="column"
					buttons={buttons}
					isSwipeable={false}
					withBackButton
					isFullScreen
					{...(!showOnMap && {
						buttonsWidget: (
							<Flex justify="space-between" p="xs">
								<Flex direction="column">
									<Text size="md" fw={600}>
										{currentLocation}
									</Text>
									{bookingPlace && (
										<Text c="dimmed" size="sm" fw={500}>
											{bookingPlace?.name}
										</Text>
									)}
								</Flex>
								<Flex align="center">
									<IconMapPin width={24} cursor="pointer" />
								</Flex>
							</Flex>
						),
					})}
				>
					{objects && (
						<PlaceMap
							parent={objects ?? []}
							objectType={objectType}
							isSingle={showOnMap}
							date={date}
						/>
					)}
				</BottomSheet>
			</>
		);

	return (
		<Box h={600}>
			{objects && (
				<PlaceMap parent={objects ?? []} objectType={objectType} isSingle={showOnMap} date={date} />
			)}
		</Box>
	);
};
